import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMyCareerReports } from '../../api/twoDays/join.api';
import { Modal, Select } from '../../components/TwoDays/Modal';
import { cn } from '../../components/TwoDays/model/derive';
import TdButton from '../../components/TwoDays/Button';
import { useWorkboardCtx } from '../../components/TwoDays/model/useWorkboard';
import { NICKNAME_MAX, randomNickname } from '../../components/TwoDays/model/nickname';
import {
  CHIP_GROUPS,
  Chip,
  TIME_ZONES,
  formatTaken,
  slotsFrom,
  toKstRanges,
  tzLabel,
} from '../../components/TwoDays/profileFields';
import HoursRanges, { slotsError } from '../../components/TwoDays/HoursRanges';
import { RingAvatar, kstArcs, ringLabel } from '../../components/TwoDays/WorkingHoursRing';

/**
 * 워크보드 Profile (W9) — 본인 작업 시간과 툴·스택을 고친다. 셀프체크는 Pre-join 에서 연결한 결과를 보여주기만 한다.
 * 작업 시간은 저장한 뒤부터 오프라인 표시·응답 시간 계산에 쓰인다 (지난 시간은 다시 계산하지 않는다).
 * 고친 채로 다른 메뉴로 가면 "Leave without saving?" 을 묻는다. 프로젝트가 끝나면 읽기 전용.
 */

const card = 'flex flex-col gap-4 rounded-2xl bg-grayscale-gray00 p-6 shadow-4';

const fromViewer = (v) => ({
  timezone: v?.timezone || 'Asia/Seoul',
  // 작업 시간 구간 여러 개 (최대 5개)
  slots: slotsFrom(v),
  tools: v?.tools ?? [],
  stack: v?.stack ?? [],
});

const same = (a, b) =>
  a.timezone === b.timezone &&
  JSON.stringify(a.slots) === JSON.stringify(b.slots) &&
  [...a.tools].sort().join('|') === [...b.tools].sort().join('|') &&
  [...a.stack].sort().join('|') === [...b.stack].sort().join('|');

/* ── Profile photo ─────────────────────────────────────── */

const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PHOTO_MAX = 5 * 1024 * 1024;
const PHOTO_ERROR = 'Use a JPG, PNG or WebP image up to 5 MB.';

/** 가운데 정사각형으로 잘라 256px JPEG data URL 로 */
const squarePhoto = (file, size = 256) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const side = Math.min(img.naturalWidth, img.naturalHeight);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const g = canvas.getContext('2d');
      g.fillStyle = '#ffffff'; // 투명한 PNG 는 흰 바탕 위에
      g.fillRect(0, 0, size, size);
      g.drawImage(
        img,
        (img.naturalWidth - side) / 2,
        (img.naturalHeight - side) / 2,
        side,
        side,
        0,
        0,
        size,
        size,
      );
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.86));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('bad image'));
    };
    img.src = url;
  });

/**
 * Profile photo (페이지 첫 카드) — 링 아바타(링 96 · 사진/이니셜 74) + 이름 + 링 설명 + 사진 올리기.
 * 사진은 고르면 바로 적용된다(페이지 Save 대상 아님) · 사이드바 아바타에도 바로.
 * 링은 Working hours 를 고치면 저장 전에도 바뀐다(미리보기). 프로젝트가 끝나면 버튼 · 도움말을 숨긴다.
 */
const PhotoCard = ({ viewer, photo, hours, now, locked }) => {
  const { dispatch, toast } = useWorkboardCtx();
  const input = useRef(null);
  const [error, setError] = useState('');
  const arcs = kstArcs(hours.slots, hours.timezone, now);

  const pick = async (file) => {
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type) || file.size > PHOTO_MAX) {
      setError(PHOTO_ERROR);
      return;
    }
    try {
      const url = await squarePhoto(file);
      dispatch({ type: 'updatePhoto', photo: url });
      setError('');
      toast?.({ title: 'Profile photo updated' });
    } catch {
      setError(PHOTO_ERROR);
    }
  };

  return (
    <section className={card}>
      <h2 className='text-[18px] font-medium text-text-primary'>Profile photo</h2>
      <div className='flex flex-wrap items-center gap-6'>
        <RingAvatar
          member={viewer}
          photo={photo}
          slots={hours.slots}
          timezone={hours.timezone}
          at={now}
          avatar={74}
          ring={96}
          textClass='text-[24px] shadow-4'
          className='m-[11px]'
        />
        <div className='flex min-w-0 flex-1 flex-col gap-2'>
          <span className='text-[15px] font-medium text-text-primary'>{viewer.name}</span>
          <span className='flex items-start gap-2 text-[13px] leading-[150%] text-text-secondary'>
            <span aria-hidden className='mt-[5px] h-2 w-2 shrink-0 rounded-full bg-green-deep02' />
            Ring shows your working hours · {ringLabel(arcs).replace('Working hours · ', '')}
          </span>
          {!locked && (
            <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
              <input
                ref={input}
                type='file'
                accept='image/jpeg,image/png,image/webp'
                className='hidden'
                onChange={(e) => {
                  pick(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
              <button
                type='button'
                onClick={() => input.current?.click()}
                className='h-9 rounded-full bg-green-deep03/[0.08] px-4 text-[14px] font-medium text-text-primary transition-colors hover:bg-green-deep03/[0.14]'
              >
                {photo ? 'Change photo' : 'Upload photo'}
              </button>
              {photo && (
                <button
                  type='button'
                  onClick={() => {
                    dispatch({ type: 'updatePhoto', photo: null });
                    setError('');
                  }}
                  className='text-[14px] font-medium text-text-secondary hover:text-text-primary'
                >
                  Remove
                </button>
              )}
              <span className='text-[12px] text-text-tertiary'>JPG, PNG or WebP, up to 5 MB</span>
            </div>
          )}
          {error && (
            <p role='alert' className='text-[13px] text-functional-failed04'>
              {error}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

/**
 * 팀에게 보일 이름 — Pre-join Consent 에서 정한 대로.
 * 실명 표시에 동의했으면 실명을 보여 주기만 하고(바꾸지 않는다), 동의하지 않았으면(Anonymous) 닉네임을 고칠 수 있다.
 * 닉네임은 Save 를 누르면 바로 적용된다 (페이지 Save 대상 아님)
 */
const NameCard = ({ viewer, me, locked }) => {
  const { dispatch, toast } = useWorkboardCtx();
  const anon = me?.nameDisplay === 'anon';
  const saved = me?.nickname ?? viewer.name;
  const [nickname, setNickname] = useState(saved);
  const trimmed = nickname.trim();
  const save = () => {
    dispatch({ type: 'updateNickname', nickname: trimmed });
    toast?.({ title: 'Nickname updated', sub: `Your team sees “${trimmed}”` });
  };
  return (
    <section className={card}>
      <div className='flex flex-col gap-1'>
        <h2 className='text-[18px] font-medium text-text-primary'>Name shown to your team</h2>
        <p className='text-[13px] leading-[150%] text-text-secondary'>
          {anon
            ? 'You chose not to show your real name in Consent, so your team sees a nickname. Your certificate still shows your real name.'
            : 'You agreed to show your real name in Consent, so your team sees your real name. This can’t be changed here.'}
        </p>
      </div>
      {anon ? (
        <div className='flex flex-col gap-1.5'>
          <label htmlFor='td-nickname' className='text-[13px] font-medium text-text-primary'>
            Nickname
          </label>
          <div className='flex flex-wrap gap-2'>
            <input
              id='td-nickname'
              value={nickname}
              maxLength={NICKNAME_MAX}
              disabled={locked}
              onChange={(e) => setNickname(e.target.value)}
              placeholder='e.g. Teal Lynx'
              className='h-11 min-w-0 flex-1 rounded-lg px-3 text-[14px] text-text-primary shadow-[inset_0_0_0_1px_rgba(0,158,79,0.2)] outline-none focus:shadow-[inset_0_0_0_1.5px_#1dc37b] disabled:bg-grayscale-gray01'
            />
            {!locked && (
              <>
                <TdButton
                  variant='soft'
                  onClick={() => setNickname(randomNickname(nickname))}
                  className='h-11 shrink-0 px-4'
                >
                  Shuffle
                </TdButton>
                <TdButton
                  onClick={save}
                  disabled={!trimmed || trimmed === saved}
                  className='h-11 shrink-0 px-5'
                >
                  Save
                </TdButton>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className='flex h-11 items-center rounded-lg bg-green-deep03/[0.04] px-3 text-[14px] text-text-primary'>
          {viewer.name}
        </div>
      )}
    </section>
  );
};

const SelfCheck = ({ reportId }) => {
  const [report, setReport] = useState(null);
  const [state, setState] = useState(reportId ? 'loading' : 'none');
  useEffect(() => {
    if (!reportId) return;
    getMyCareerReports()
      .then((list) => {
        const found = (Array.isArray(list) ? list : []).find(
          (r) => String(r.id) === String(reportId),
        );
        setReport(found ?? null);
        setState(found ? 'ready' : 'none');
      })
      .catch(() => setState('error'));
  }, [reportId]);

  return (
    <section className={card}>
      <div className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1'>
        <h2 className='text-[16px] font-medium text-text-primary'>Self-check</h2>
        {report?.createdAt && (
          <span className='text-[13px] text-text-secondary'>
            Taken {formatTaken(report.createdAt)}
          </span>
        )}
      </div>
      {state === 'loading' && (
        <div
          className='h-5 w-2/3 animate-pulse rounded bg-green-deep03/[0.06]'
          aria-label='Loading'
        />
      )}
      {state === 'ready' && (
        <p className='text-[14px] leading-[150%] text-text-primary'>
          {report.summary ||
            [report.careerType, report.matchPct ? `${report.matchPct}% match` : null]
              .filter(Boolean)
              .join(' · ')}
        </p>
      )}
      {state === 'none' && (
        <p className='text-[14px] text-text-secondary'>No self-check linked to this project.</p>
      )}
      {state === 'error' && (
        <p className='text-[14px] text-text-secondary'>We couldn’t load your self-check.</p>
      )}
    </section>
  );
};

const TwoDaysProfile = () => {
  const { data, viewer, dispatch, now, readOnly } = useWorkboardCtx();
  const navigate = useNavigate();
  const { hash } = useLocation();
  // 사이드바 "Edit work hours" → Working hours 로 내린다
  useEffect(() => {
    if (hash === '#work-hours') {
      document.getElementById('work-hours')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);
  const completed = data.project.status === 'ended';
  const locked = completed || readOnly;

  const saved = useMemo(() => fromViewer(viewer), [viewer]);
  const [form, setFormState] = useState(saved);
  const setForm = (patch) => setFormState((f) => ({ ...f, ...patch }));
  const [leaveTo, setLeaveTo] = useState(null);
  // Other — 목록에 없는 툴을 직접 적는다 (툴에 더해진다)
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState('');

  // 다른 사람으로 바뀌면(개발용 보기 전환) 새로 채운다
  const viewerId = viewer?.id;
  useEffect(() => setFormState(fromViewer(viewer)), [viewerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !locked && !same(form, saved);
  const picked = form.tools.length + form.stack.length;
  const hoursError = slotsError(form.slots);
  const canSave = dirty && picked > 0 && !hoursError;

  // 고친 채로 나가려 하면 묻는다 — 화면 안 링크는 클릭을 가로채고, 새로고침·닫기는 브라우저 확인창
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    const onClick = (e) => {
      if (!dirtyRef.current || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest?.('a[href]');
      if (!a || a.target === '_blank') return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname)
        return;
      e.preventDefault();
      e.stopPropagation();
      setLeaveTo(url.pathname + url.search);
    };
    const onUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    document.addEventListener('click', onClick, true);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);

  // 뒤로가기 — 고친 채면 같은 주소로 한 칸을 더 쌓아 두고, 뒤로 가면 시스템 확인창으로 묻는다
  const guardRef = useRef(false);
  useEffect(() => {
    if (dirty && !guardRef.current) {
      window.history.pushState(window.history.state, '', window.location.href);
      guardRef.current = true;
    } else if (!dirty && guardRef.current) {
      // 저장하거나 원래대로 돌리면 쌓아 둔 칸을 걷는다
      guardRef.current = false;
      window.history.back();
    }
  }, [dirty]);
  useEffect(() => {
    const onPop = () => {
      if (!guardRef.current) return;
      if (window.confirm('Leave without saving?\nYour profile changes will be lost.')) {
        guardRef.current = false;
        dirtyRef.current = false;
        window.history.back();
      } else {
        window.history.pushState(window.history.state, '', window.location.href);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (!viewer || viewer.role === 'npc') {
    return (
      <div className='flex max-w-[880px] flex-col gap-6'>
        <h1 className='text-[28px] font-medium leading-[120%] text-text-primary'>Profile</h1>
        <p className='text-[14px] text-text-secondary'>
          You’re viewing this board as staff, so there’s no profile here.
        </p>
      </div>
    );
  }

  const save = () => {
    if (!canSave) return;
    dispatch({
      type: 'updateProfile',
      timezone: form.timezone,
      // 첫 구간은 예전 필드로도 보낸다 (여러 구간을 모르는 화면 · 계산 호환)
      availableFrom: form.slots[0].from,
      availableTo: form.slots[0].to,
      availableSlots: form.slots,
      tools: form.tools,
      stack: form.stack,
    });
  };

  const zones = TIME_ZONES.includes(form.timezone) ? TIME_ZONES : [form.timezone, ...TIME_ZONES];
  const kst = toKstRanges(form.timezone, form.slots, now);
  const addOther = () => {
    const value = otherText.trim().slice(0, 60);
    if (!value) return;
    const exists = [...form.tools, ...form.stack].some(
      (x) => x.toLowerCase() === value.toLowerCase(),
    );
    if (!exists) setForm({ tools: [...form.tools, value] });
    setOtherText('');
  };
  const toggle = (key, item) =>
    setForm({
      [key]: form[key].includes(item) ? form[key].filter((x) => x !== item) : [...form[key], item],
    });

  return (
    <div className='flex max-w-[880px] flex-col gap-6 pb-24 sm:pb-0'>
      <h1 className='text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
        Profile
      </h1>

      {completed && (
        <p className='rounded-xl border border-green-deep03/[0.12] bg-surface-green10 px-4 py-3 text-[13px] text-text-primary'>
          This project is completed, so your profile is read-only.
        </p>
      )}

      <PhotoCard viewer={viewer} photo={data.me?.photo} hours={form} now={now} locked={completed} />
      {data.me?.nameDisplay && (
        <NameCard viewer={viewer} me={data.me} locked={completed || readOnly} />
      )}

      {/* 작업 시간 */}
      {/* 사이드바 오프라인 안내의 "Edit work hours" 가 여기로 온다 */}
      <section id='work-hours' className={cn(card, 'scroll-mt-24')}>
        <h2 className='text-[16px] font-medium text-text-primary'>Working hours</h2>
        <div className='grid gap-3 sm:max-w-[360px]'>
          <label className='flex flex-col gap-1.5'>
            <span className='text-[13px] text-text-secondary'>Time zone</span>
            <Select
              value={form.timezone}
              disabled={locked}
              onChange={(e) => setForm({ timezone: e.target.value })}
            >
              {zones.map((tz) => (
                <option key={tz} value={tz}>
                  {tzLabel(tz)}
                </option>
              ))}
            </Select>
          </label>
        </div>
        <div className='flex flex-col gap-1.5'>
          <span className='text-[13px] text-text-secondary'>Hours (your time zone)</span>
          <HoursRanges
            slots={form.slots}
            onChange={(slots) => setForm({ slots })}
            fieldClassName='h-11 w-[124px]'
            disabled={locked}
          />
        </div>
        <div className='flex flex-col gap-1 rounded-xl bg-green-deep03/[0.04] px-4 py-3'>
          <span className='text-[12px] text-text-secondary'>In Seoul time (KST)</span>
          <span className='text-[18px] font-medium text-text-primary'>
            {kst.text} KST
            {kst.crossesDay && (
              <span className='ml-2 text-[13px] font-normal text-text-secondary'>
                ends next day
              </span>
            )}
          </span>
        </div>
        {hoursError && !locked ? (
          <p className='text-[13px] text-functional-failed04'>{hoursError}</p>
        ) : (
          <p className='text-[13px] text-text-secondary'>
            Changes apply from now on to your offline status and response time. Past hours aren’t
            recalculated.
          </p>
        )}
      </section>

      {/* 툴·스택 */}
      <section className={card}>
        <div className='flex flex-col gap-0.5'>
          <h2 className='text-[16px] font-medium text-text-primary'>Tools and stack</h2>
          {!locked && <p className='text-[13px] text-text-secondary'>Pick at least one.</p>}
        </div>
        <div className='flex flex-wrap gap-2'>
          {CHIP_GROUPS.flatMap((g) => {
            const items = [...g.items, ...form[g.key].filter((x) => !g.items.includes(x))];
            return items
              .filter((item) => !locked || form[g.key].includes(item))
              .map((item) =>
                locked ? (
                  <span
                    key={`${g.key}-${item}`}
                    className='flex h-9 items-center rounded-full bg-surface-green10 px-4 text-[14px] font-medium text-green-deep04'
                  >
                    {item}
                  </span>
                ) : (
                  <Chip
                    key={`${g.key}-${item}`}
                    on={form[g.key].includes(item)}
                    onClick={() => toggle(g.key, item)}
                  >
                    {item}
                  </Chip>
                ),
              );
          })}
          {!locked && (
            <Chip on={otherOpen} onClick={() => setOtherOpen((v) => !v)}>
              Other
            </Chip>
          )}
        </div>
        {!locked && otherOpen && (
          <div className='flex gap-2'>
            <input
              autoFocus
              value={otherText}
              maxLength={60}
              placeholder='Type a tool and press Enter'
              onChange={(e) => setOtherText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  addOther();
                }
              }}
              className='h-10 min-w-0 flex-1 rounded-lg border border-green-deep03/[0.2] bg-grayscale-gray00 px-3 text-[14px] text-text-primary outline-none placeholder:text-text-placeholder focus:border-green-deep02'
            />
            <TdButton
              variant='soft'
              disabled={!otherText.trim()}
              onClick={addOther}
              className='shrink-0 px-4'
            >
              Add
            </TdButton>
          </div>
        )}
        {!locked && picked === 0 && (
          <p className='text-[13px] text-functional-failed04'>Pick at least one tool or stack.</p>
        )}
      </section>

      <SelfCheck reportId={data.me?.careerReportId} />

      {/* 저장 — 모바일은 아래에 붙는다 */}
      {!locked && (
        <div className='fixed inset-x-0 bottom-0 z-20 flex items-center justify-end gap-3 border-t border-green-deep03/[0.08] bg-grayscale-gray00 px-4 py-3 sm:static sm:border-0 sm:bg-transparent sm:p-0'>
          {!dirty && (
            <span className='hidden text-[13px] text-text-secondary sm:inline'>
              No changes to save
            </span>
          )}
          <TdButton disabled={!canSave} onClick={save} className='h-11 px-6 max-sm:w-full sm:h-10'>
            Save
          </TdButton>
        </div>
      )}

      <Modal
        open={leaveTo != null}
        title='Leave without saving?'
        subtitle='Your profile changes will be lost.'
        onClose={() => setLeaveTo(null)}
        footer={
          <>
            <TdButton variant='soft' onClick={() => setLeaveTo(null)} className='px-4'>
              Keep editing
            </TdButton>
            <TdButton
              onClick={() => {
                const to = leaveTo;
                dirtyRef.current = false;
                guardRef.current = false;
                setLeaveTo(null);
                // 뒤로가기용으로 쌓아 둔 칸을 덮어써 기록에 Profile 이 두 번 남지 않게 한다
                navigate(to, { replace: true });
              }}
              className='px-4'
            >
              Leave
            </TdButton>
          </>
        }
      >
        {null}
      </Modal>
    </div>
  );
};

export default TwoDaysProfile;
